/**
 * Observability config generation for aaac init / update.
 *
 * Resolves the declarative `event_mapping` binding and the artifact-contracts
 * path globs into the deterministic JSON lookup tables consumed at record time
 * by `.cursor/hooks/observ-record.sh` and `@aaac/observability`:
 *
 *   .agent-logs/config/event-mapping.json   — resolved hook → span/link rules
 *   .agent-logs/config/artifact-lookup.json  — artifact_id ← path glob (longest-match)
 *   .agent-logs/config/task-patterns.json    — structured tag → field mapping
 *
 * See docs/observability-spec/shift-left-event-mapping.md §6.3 / §7.
 *
 * Self-contained (no @aaac/observability dependency) to keep the CLI package
 * free of the observability runtime/OTEL dependency — mirroring the same
 * "avoid cross-package coupling" rationale documented in governance.ts. The
 * generated JSON is the contract: it must stay loadable by
 * @aaac/observability's loadEventMappingConfig() / lookupArtifact(), and the
 * generators below must be kept in sync with
 * packages/observability/src/event-mapping/lookup.ts.
 *
 * Writes are byte-deterministic (stable key order from the source YAML, no
 * timestamps) so repeated `aaac update` runs leave the files unchanged.
 */

import { existsSync } from "node:fs";
import { mkdir, readFile, writeFile } from "node:fs/promises";
import { dirname, join, resolve } from "node:path";
import { parse as parseYaml } from "yaml";
import { z } from "zod";

// ---------------------------------------------------------------------------
// Paths (relative to project root)
// ---------------------------------------------------------------------------

export const OBSERV_CONFIG_DIR = ".agent-logs/config";
export const EVENT_MAPPING_JSON = `${OBSERV_CONFIG_DIR}/event-mapping.json`;
export const ARTIFACT_LOOKUP_JSON = `${OBSERV_CONFIG_DIR}/artifact-lookup.json`;
export const TASK_PATTERNS_JSON = `${OBSERV_CONFIG_DIR}/task-patterns.json`;

// ---------------------------------------------------------------------------
// Types (mirror packages/observability/src/event-mapping/lookup.ts &
// packages/observability/src/enrich/api.ts — keep in sync)
// ---------------------------------------------------------------------------

/** One structured-tag → canonical field mapping (shift-left §3.2). */
export interface TaskPattern {
  tag_prefix: string;
  field: string;
}

/** One artifact_id ← glob entry (shift-left §3.3). */
export interface ArtifactPattern {
  artifact_id: string;
  pattern: string;
}

export interface GeneratedConfigFile {
  path: string;
  action: "created" | "updated" | "unchanged";
}

// ---------------------------------------------------------------------------
// Generators (mirror lookup.ts — keep in sync)
// ---------------------------------------------------------------------------

/**
 * Default structured-tag patterns parsed from `subagentStart.task`
 * (shift-left-event-mapping.md §3.2). Mirrors generateTaskPatterns() in
 * packages/observability/src/event-mapping/lookup.ts.
 */
export function generateTaskPatterns(): TaskPattern[] {
  return [
    { tag_prefix: "DSL_TASK", field: "task.id" },
    { tag_prefix: "AGENT_ROLE", field: "task.target_agent" },
    { tag_prefix: "WORKFLOW_PHASE", field: "workflow.phase" },
  ];
}

/**
 * Flatten artifact declarations into per-glob lookup entries
 * (shift-left-event-mapping.md §3.3). Mirrors generateArtifactLookup() in
 * packages/observability/src/event-mapping/lookup.ts. Declaration order is
 * preserved so the generated JSON is deterministic.
 */
export function generateArtifactLookup(
  artifactDeclarations: Array<{ artifact_id: string; path_patterns: string[] }>,
): ArtifactPattern[] {
  const patterns: ArtifactPattern[] = [];
  for (const decl of artifactDeclarations) {
    for (const pattern of decl.path_patterns) {
      patterns.push({ artifact_id: decl.artifact_id, pattern });
    }
  }
  return patterns;
}

// ---------------------------------------------------------------------------
// Binding / artifact-contracts loaders
// ---------------------------------------------------------------------------

/**
 * Extract the hook → span/link rules from an observability binding file.
 * Accepts both `event_mapping:` (binding form) and `mappings:` (resolved form)
 * top-level keys, matching @aaac/observability's loadEventMappingConfig().
 * Returns the rules object as parsed (insertion order preserved by `yaml`).
 */
export function extractEventMappingRules(
  parsed: unknown,
): Record<string, unknown> {
  if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
    const obj = parsed as Record<string, unknown>;
    const rules =
      (obj.event_mapping as Record<string, unknown> | undefined) ??
      (obj.mappings as Record<string, unknown> | undefined);
    if (rules && typeof rules === "object") {
      return rules;
    }
  }
  return {};
}

const ArtifactContractsSchema = z.object({
  artifacts: z
    .record(
      z.string(),
      z
        .object({ path_patterns: z.array(z.string()).optional() })
        .passthrough(),
    )
    .optional(),
});

/**
 * Load artifact-contracts.yaml and return the declarations that carry path
 * globs, in declaration order. Artifacts without `path_patterns` are skipped.
 */
export function parseArtifactDeclarations(
  parsed: unknown,
): Array<{ artifact_id: string; path_patterns: string[] }> {
  const { artifacts } = ArtifactContractsSchema.parse(parsed ?? {});
  if (!artifacts) return [];

  const decls: Array<{ artifact_id: string; path_patterns: string[] }> = [];
  for (const [artifact_id, decl] of Object.entries(artifacts)) {
    if (decl.path_patterns && decl.path_patterns.length > 0) {
      decls.push({ artifact_id, path_patterns: decl.path_patterns });
    }
  }
  return decls;
}

// ---------------------------------------------------------------------------
// Deterministic JSON write (idempotent)
// ---------------------------------------------------------------------------

function serialize(value: unknown): string {
  return JSON.stringify(value, null, 2) + "\n";
}

/**
 * Write `value` as pretty JSON only when the on-disk content differs.
 * Returns the action so callers can report and lock the entry.
 */
async function writeJsonIfChanged(
  absPath: string,
  value: unknown,
): Promise<GeneratedConfigFile["action"]> {
  const serialized = serialize(value);
  const existed = existsSync(absPath);
  const existing = existed ? await readFile(absPath, "utf8") : undefined;

  if (existing === serialized) {
    return "unchanged";
  }

  await mkdir(dirname(absPath), { recursive: true });
  await writeFile(absPath, serialized, "utf8");
  return existed ? "updated" : "created";
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface GenerateObservConfigOptions {
  projectRoot: string;
  /** Path to the observability event_mapping binding (relative to projectRoot or absolute). */
  eventMapping?: string;
  /** Path to artifact-contracts.yaml (relative to projectRoot or absolute). */
  artifactContracts?: string;
}

/**
 * Generate the .agent-logs/config/*.json lookup tables from the declarative
 * binding + artifact-contracts. Idempotent and deterministic.
 *
 * - event-mapping.json   is written when `eventMapping` resolves to an existing file.
 * - artifact-lookup.json is written when `artifactContracts` resolves to an existing file.
 * - task-patterns.json   is always written (constant content).
 *
 * Returns the generated files (for reporting) and lock entries so the caller
 * can register them for `aaac uninstall`.
 */
export async function generateObservConfig(
  options: GenerateObservConfigOptions,
): Promise<{
  generated: GeneratedConfigFile[];
  lockEntries: Array<{ path: string; type: "generated" }>;
}> {
  const root = resolve(options.projectRoot);
  const generated: GeneratedConfigFile[] = [];

  // --- event-mapping.json ---
  if (options.eventMapping) {
    const bindingPath = resolve(root, options.eventMapping);
    if (existsSync(bindingPath)) {
      const parsed = parseYaml(await readFile(bindingPath, "utf8")) as unknown;
      const mappings = extractEventMappingRules(parsed);
      const action = await writeJsonIfChanged(
        join(root, EVENT_MAPPING_JSON),
        { mappings },
      );
      generated.push({ path: EVENT_MAPPING_JSON, action });
    }
  }

  // --- artifact-lookup.json ---
  if (options.artifactContracts) {
    const contractsPath = resolve(root, options.artifactContracts);
    if (existsSync(contractsPath)) {
      const parsed = parseYaml(await readFile(contractsPath, "utf8")) as unknown;
      const lookup = generateArtifactLookup(parseArtifactDeclarations(parsed));
      const action = await writeJsonIfChanged(
        join(root, ARTIFACT_LOOKUP_JSON),
        lookup,
      );
      generated.push({ path: ARTIFACT_LOOKUP_JSON, action });
    }
  }

  // --- task-patterns.json (constant) ---
  {
    const action = await writeJsonIfChanged(
      join(root, TASK_PATTERNS_JSON),
      generateTaskPatterns(),
    );
    generated.push({ path: TASK_PATTERNS_JSON, action });
  }

  const lockEntries = generated.map((g) => ({
    path: g.path,
    type: "generated" as const,
  }));

  return { generated, lockEntries };
}
